"use client";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { useTranslation } from "react-i18next";

interface PlanChangeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  changeType: "upgrade" | "downgrade";
  currentPlan: string;
  newPlan: string;
  currentPlanVideos: number;
  newPlanVideos: number;
}

export function PlanChangeDialog({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  changeType,
  currentPlan,
  newPlan,
  currentPlanVideos,
  newPlanVideos,
}: PlanChangeDialogProps) {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const backdropRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Sync internal state with prop
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
    }
  }, [isOpen]);

  useGSAP(() => {
    if (isOpen) {
      const tl = gsap.timeline();
      
      tl.fromTo(backdropRef.current, 
        { opacity: 0 }, 
        { opacity: 1, duration: 0.3, ease: "power2.out" }
      );
      
      tl.fromTo(dialogRef.current, 
        { scale: 0.9, opacity: 0, y: 20, rotateX: -10 }, 
        { scale: 1, opacity: 1, y: 0, rotateX: 0, duration: 0.5, ease: "back.out(1.7)" },
        "-=0.2"
      );
    } else if (shouldRender) {
      // Handle exit animation
      const tl = gsap.timeline({
        onComplete: () => setShouldRender(false)
      });

      tl.to(dialogRef.current, {
        scale: 0.9,
        opacity: 0,
        y: 10,
        duration: 0.2,
        ease: "power2.in"
      });

      tl.to(backdropRef.current, {
        opacity: 0,
        duration: 0.2,
        ease: "power2.in"
      }, "-=0.1");
    }
  }, { scope: containerRef, dependencies: [isOpen] });

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (shouldRender) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [shouldRender]);

  if (!shouldRender) return null;

  const isUpgrade = changeType === "upgrade";

  const dialogContent = (
    <div
      ref={containerRef}
      className="fixed inset-0 z-9999 flex items-center justify-center perspective-[1000px]"
      style={{ perspective: "1000px" }}
    >
      {/* Backdrop */}
      <div 
        ref={backdropRef}
        className="absolute inset-0 z-0 bg-black/60" 
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "relative z-10 w-full max-w-md mx-4 bg-white rounded-lg shadow-2xl",
          "p-6 space-y-6",
          "2k:max-w-lg 2k:p-10 2k:space-y-8",
          "4k:max-w-xl 4k:p-14 4k:space-y-10"
        )}
      >
        {/* Header */}
        <div className="space-y-2">
          <h2 className={cn(
            "text-2xl font-bold",
            "2k:text-3xl",
            "4k:text-4xl"
          )}>
            {isUpgrade ? t("planChangeDialog.upgradeTitle") : t("planChangeDialog.downgradeTitle")}
          </h2>
          <p className={cn(
            "text-gray-600",
            "2k:text-lg",
            "4k:text-xl"
          )}>
            {currentPlan} → {newPlan}
          </p>
        </div>

        {/* Content */}
        <div className={cn(
          "p-4 rounded-lg space-y-3",
          "2k:p-6 2k:space-y-4",
          "4k:p-8 4k:space-y-5",
          isUpgrade ? "bg-green-50" : "bg-amber-50"
        )}>
          {isUpgrade ? (
            <>
              <div className="flex items-center gap-2">
                <span className="text-green-600 text-xl">✓</span>
                <span className={cn(
                  "font-medium",
                  "2k:text-lg",
                  "4k:text-xl"
                )}>
                  {t("planChangeDialog.immediateEffect")}
                </span>
              </div>
              <p className={cn(
                "text-gray-700",
                "2k:text-base",
                "4k:text-lg"
              )}>
                {t("planChangeDialog.upgradeQuotaDesc", { count: newPlanVideos })}
              </p>
              <p className={cn(
                "text-sm text-gray-500",
                "2k:text-base",
                "4k:text-lg"
              )}>
                {t("planChangeDialog.prorateNote")}
              </p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <span className="text-amber-600 text-xl">⏳</span>
                <span className={cn(
                  "font-medium",
                  "2k:text-lg",
                  "4k:text-xl"
                )}>
                  {t("planChangeDialog.nextPeriodEffect")}
                </span>
              </div>
              <p className={cn(
                "text-gray-700",
                "2k:text-base",
                "4k:text-lg"
              )}>
                {t("planChangeDialog.downgradeKeepQuota", { count: currentPlanVideos })}
              </p>
              <p className={cn(
                "text-sm text-gray-500",
                "2k:text-base",
                "4k:text-lg"
              )}>
                {t("planChangeDialog.downgradeNewQuota", { count: newPlanVideos })}
              </p>
            </>
          )}
        </div>

        {/* Actions */}
        <div className={cn(
          "flex gap-3",
          "2k:gap-4",
          "4k:gap-5"
        )}>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className={cn(
              "flex-1 p-6 rounded-md cursor-pointer",
              "2k:p-8 2k:text-lg",
              "4k:p-10 4k:text-xl"
            )}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading}
            className={cn(
              "flex-1 p-6 rounded-md cursor-pointer",
              "2k:p-8 2k:text-lg",
              "4k:p-10 4k:text-xl",
              isUpgrade ? "bg-black hover:bg-black/90" : "bg-amber-600 hover:bg-amber-700"
            )}
          >
            {isLoading ? t("common.loading") : isUpgrade ? t("planChangeDialog.upgradeNow") : t("planChangeDialog.confirmNextPeriod")}
          </Button>
        </div>
      </div>
    </div>
  );

  // Use portal to render at document.body level for proper positioning
  return createPortal(dialogContent, document.body);
}
